import { timezoneOptions } from 'shared/lib/timezones';
import {
  isCampaignType,
  isCampaignStatus,
  getCampaignTypeName,
  CAMPAIGN_STATUS_BADGE_PROPS,
} from 'entities/campaign/@x';
import { isPromoType, PromoTypes, isPromoStatus } from 'entities/contractor';
import { Badge } from 'shared/ui/Badge';
import { ClippedText } from 'shared/ui/ClippedText';
import { formatPhone, formatPrice, capitalizeFirstLetter } from 'shared/lib/formatting';
import { getKeys } from 'shared/lib/object';
import { isNumber, isObject, isString } from 'shared/lib/type-guards';
import {
  CrmHistoryRowKey,
  HistoryContractorRowKey,
  HistoryCampaignRowKey,
  HistoryPromoRowKey,
  HistoryGroupRowKey,
  type HistoryCampaignData,
  type HistoryCrmData,
  type HistoryContractorData,
  type HistoryPromoData,
  type HistoryGroupData,
  type HistoryInvoiceData,
  CrmHistoryRowValue,
  InvoiceHistoryRowKey,
} from 'entities/history';
import { formatTrafficDays } from 'shared/lib/format';

type HistoryEventData = HistoryCampaignData | HistoryPromoData | HistoryContractorData | HistoryCrmData;

const wrapToClippedText = (value: string) => {
  return (
    <ClippedText
      text={value}
      tooltip={{ content: value }}
    />
  );
};

const transformPromoEventData = (promoHistory: HistoryPromoData) => {
  const mixedPromoData = {
    company_name: promoHistory.contractor.company_name,
    ...promoHistory.promo,
  };

  return getKeys(mixedPromoData).map((promoHistoryKey) => {
    let valueToDisplay;
    const promoChange = mixedPromoData[promoHistoryKey];

    switch (promoHistoryKey as keyof typeof HistoryPromoRowKey) {
      case 'type':
        valueToDisplay = isPromoType(promoChange) && PromoTypes[promoChange];
        break;
      case 'status':
        valueToDisplay = isPromoStatus(promoChange) ? capitalizeFirstLetter(promoChange) : '';
        break;
      case 'launch_budget':
      case 'spent_launch_budget':
      case 'budget':
      case 'spent_budget':
        valueToDisplay = isNumber(promoChange) ? formatPrice(promoChange) : '';
        break;

      default:
        valueToDisplay = isString(promoChange) ? wrapToClippedText(promoChange) : promoChange;
        break;
    }

    return {
      key: HistoryPromoRowKey[promoHistoryKey],
      value: valueToDisplay,
    };
  });
};

type CampaignChangeType = string | number | boolean | number[];

function statusCase(campaignChange?: CampaignChangeType) {
  return isCampaignStatus(campaignChange) ? <Badge {...CAMPAIGN_STATUS_BADGE_PROPS[campaignChange]} /> : '';
}

function isActiveCase(campaignChange?: CampaignChangeType) {
  return campaignChange ? 'Yes' : 'No';
}

function bidsCase(campaignChange?: CampaignChangeType) {
  if (Array.isArray(campaignChange)) {
    return campaignChange.map((bid) => `${formatPrice(bid)}`).join(' / ') || '';
  }
  if (isNumber(campaignChange)) {
    return formatPrice(campaignChange);
  }
}

function weeklyBudgetCase(campaignChange?: CampaignChangeType) {
  return isString(campaignChange) ? formatPrice(campaignChange) : '';
}

function typeCase(campaignChange?: CampaignChangeType) {
  return isCampaignType(campaignChange) ? getCampaignTypeName(campaignChange) : 'Unknown type';
}

function zipCodesCase(campaignChange?: CampaignChangeType) {
  if (isNumber(campaignChange)) {
    return new Intl.NumberFormat('en-US').format(campaignChange);
  }
}

function defaultCase(campaignChange?: CampaignChangeType) {
  return isString(campaignChange) ? wrapToClippedText(campaignChange) : campaignChange;
}

function priceCase(campaignChange?: CampaignChangeType) {
  return isString(campaignChange) ? formatPrice(campaignChange) : '';
}

function plainTextCase(campaignChange?: CampaignChangeType) {
  return <span>{campaignChange}</span>;
}

const transformCampaignEventData = (campaignHistory: HistoryCampaignData) => {
  return getKeys(campaignHistory).map((campaignHistoryKey) => {
    let valueToDisplay;
    const campaignChange = campaignHistory[campaignHistoryKey];

    switch (campaignHistoryKey) {
      case 'status':
        valueToDisplay = statusCase(campaignChange);
        break;
      case 'is_active':
        valueToDisplay = isActiveCase(campaignChange);
        break;
      case 'bids':
        valueToDisplay = bidsCase(campaignChange);
        break;
      case 'weekly_budget':
        valueToDisplay = weeklyBudgetCase(campaignChange);
        break;
      case 'type':
        valueToDisplay = typeCase(campaignChange);
        break;

      case 'zip_codes':
        if (zipCodesCase(campaignChange)) {
          valueToDisplay = zipCodesCase(campaignChange);
        }
        break;

      default:
        valueToDisplay = defaultCase(campaignChange);
        break;
    }

    return {
      key: HistoryCampaignRowKey[campaignHistoryKey],
      value: valueToDisplay,
    };
  });
};

const transformContractorEventData = (contractorHistory: HistoryContractorData) => {
  return getKeys(contractorHistory).map((contractorHistoryKey) => {
    let valueToDisplay;
    const contractorChange = contractorHistory[contractorHistoryKey];

    switch (contractorHistoryKey) {
      case 'phoneNumber':
        valueToDisplay = isString(contractorChange) ? formatPhone(contractorChange) : '';
        break;
      case 'timezone':
        valueToDisplay = timezoneOptions.find((tz) => tz.id === contractorChange)?.text;
        break;
      case 'salesRep':
        valueToDisplay = isObject(contractorChange)
          ? wrapToClippedText(contractorChange.fullName)
          : 'No data is available';
        break;

      default:
        valueToDisplay = isString(contractorChange) ? wrapToClippedText(contractorChange) : 'No data is available';
        break;
    }

    return {
      key: HistoryContractorRowKey[contractorHistoryKey],
      value: valueToDisplay,
    };
  });
};

const transformGroupEventData = (groupHistory: HistoryGroupData) => {
  return getKeys(groupHistory).map((groupHistoryKey) => {
    let valueToDisplay;
    const groupChange = groupHistory[groupHistoryKey];

    switch (groupHistoryKey) {
      case 'excludedZipCodes':
        valueToDisplay = isNumber(groupChange) && groupChange.toString();
        break;
      case 'googleNetwork':
        valueToDisplay = groupChange ? 'Applied' : 'Not applied';
        break;
      case 'trafficDays':
        valueToDisplay = isString(groupChange) && formatTrafficDays(groupChange, true);
        break;

      default:
        valueToDisplay = isString(groupChange) ? wrapToClippedText(groupChange) : 'No data is available';
        break;
    }

    return {
      key: HistoryGroupRowKey[groupHistoryKey],
      value: valueToDisplay,
    };
  });
};

const transformCrmEventData = (crmHistory: HistoryCrmData) => {
  return getKeys(crmHistory).map((crmHistoryKey) => {
    const value = crmHistory[crmHistoryKey] ?? '';

    return {
      key: CrmHistoryRowKey[crmHistoryKey],
      value: CrmHistoryRowValue[value] || value,
    };
  });
};

const transformInvoiceEventData = (campaignHistory: HistoryInvoiceData) => {
  return getKeys(campaignHistory).map((campaignHistoryKey) => {
    let valueToDisplay;
    const campaignChange = campaignHistory[campaignHistoryKey];

    switch (campaignHistoryKey) {
      case 'discount':
      case 'total':
        valueToDisplay = priceCase(campaignChange);
        break;
      default:
        valueToDisplay = plainTextCase(campaignChange);
    }

    return {
      key: InvoiceHistoryRowKey[campaignHistoryKey],
      value: valueToDisplay,
    };
  });
};

const isContractorEvent = (event: HistoryEventData): event is HistoryContractorData => {
  return isObject(event) && 'manager' in event && 'paymentMethod' in event;
};

const isCampaignEvent = (data: HistoryEventData): data is HistoryCampaignData => {
  return isObject(data) && 'zip_codes' in data;
};

const isPromoEvent = (data: HistoryEventData): data is HistoryPromoData => {
  return isObject(data) && 'promo' in data;
};

const isCrmEvent = (data: HistoryEventData): data is HistoryCrmData => {
  return isObject(data) && 'integrationAlias' in data;
};

const isGroupEvent = (data: HistoryEventData): data is HistoryGroupData => {
  return isObject(data) && 'trafficTime' in data;
};

const isInvoiceEvent = (data: HistoryEventData): data is HistoryGroupData => {
  return isObject(data) && 'invoiceNumber' in data;
};

export const transformEventData = (changeData: HistoryEventData) => {
  if (isCampaignEvent(changeData)) {
    return transformCampaignEventData(changeData);
  }

  if (isPromoEvent(changeData)) {
    return transformPromoEventData(changeData);
  }

  if (isContractorEvent(changeData)) {
    return transformContractorEventData(changeData);
  }

  if (isCrmEvent(changeData)) {
    return transformCrmEventData(changeData);
  }

  if (isGroupEvent(changeData)) {
    return transformGroupEventData(changeData);
  }

  if (isInvoiceEvent(changeData)) {
    return transformInvoiceEventData(changeData);
  }

  throw new Error('Unknown event type in SeveralRows Details');
};
